import { TIMELINE_DASH } from '@pages/Itinerary/timelineRail.styles'
import styled from 'styled-components'
import { horizontalRailMobile } from './TripOverview.styles'
import { darkOrange, blackOrange, black, textMuted, white, warmTint } from '@/theme/colors'
import { radii, breakpoints, media } from '@/theme/tokens'
import { text, leading } from '@/theme/typography'
import { innerCardSurface } from '@components/Card'

// Segment-chip palette lifted from the itinerary screen
// (`TravelSegment.styles.ts`) so the preview reads as the same surface; the
// rail line + dash colour come from the shared `timelineRail.styles`.
const SEGMENT_CHIP_BG = warmTint
const SEGMENT_ICON = darkOrange
const SEGMENT_TEXT = blackOrange

export const PreviewRow = styled.div`
	display: flex;
	gap: 14px;
	width: 100%;

	/* Desktop (the tall two-row tile): the preview must never drive the grid's
	   row heights — the money + ideas tiles size the rows and the timeline
	   clips to however many cards fit ("as many items as can fit").
	   \`contain: size\` zeroes the intrinsic contribution; the min-height keeps
	   a usable preview when the right column runs short. */
	@media (min-width: ${breakpoints.bentoCollapse + 1}px) {
		contain: size;
		min-height: 260px;
		overflow: hidden;
	}

	/* Phone: the preview becomes the shared horizontal rail of fixed
	   ~250px day columns (8px bottom breathing room per the handoff m-rail). */
	${media.phone} {
		${horizontalRailMobile(8)}
	}
`

export const DayColumn = styled.div`
	flex: 1;
	min-width: 0;

	${media.phone} {
		flex: 0 0 250px;
		width: 250px;
		min-width: 250px;
	}
`

export const DayHead = styled.div`
	display: flex;
	align-items: baseline;
	gap: 8px;
	margin-bottom: 8px;
`

export const DayName = styled.span`
	${text.label}
	color: ${black};
`

export const DayDate = styled.span`
	${text.caption}
	color: ${textMuted};
`

export const Stops = styled.div`
	display: flex;
	flex-direction: column;
	min-width: 0;
`

/** Dot centre below a stop row's top: the 14px lead-in + half the 10px dot. */
export const STOP_ANCHOR = 19
/** Chip centre below a segment row's top: the 6px pad + half the 24px chip. */
export const SEGMENT_ANCHOR = 18

/**
 * The 24px timeline column beside each card — same geometry as the itinerary
 * screen's `EventDotColumn`. Hosts the absolutely-positioned `RailConnector`
 * that draws the dashes down to the next row's anchor.
 */
export const Rail = styled.span`
	width: 24px;
	flex-shrink: 0;
	position: relative;
	display: flex;
	flex-direction: column;
	align-items: center;
	padding-top: 14px;
`

/* position: relative → paints above the connector runs. */
export const Dot = styled.span`
	width: 10px;
	height: 10px;
	border-radius: ${radii.round};
	border: 2px solid ${TIMELINE_DASH};
	background: ${white};
	flex-shrink: 0;
	position: relative;
`

export const Stop = styled.div`
	display: flex;
	gap: 10px;
`

/** The same inner card as the itinerary board, at the preview's tighter padding. */
export const EventCard = styled.div`
	${innerCardSurface}
	flex: 1;
	min-width: 0;
	padding: 11px 12px;
	margin-bottom: 10px;
	display: flex;
	flex-direction: column;
	gap: 6px;
`

export const EventTitle = styled.div`
	${text.label}
	line-height: ${leading.snug};
	color: ${black};
	overflow: hidden;
	display: -webkit-box;
	-webkit-line-clamp: 2;
	-webkit-box-orient: vertical;
`

export const MetaRow = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	margin-top: 2px;
	color: ${textMuted};
`

/**
 * The compact travel row between two cards — the itinerary screen's
 * `TravelSegment` reduced to its rail chip + one info line. No "Directions"
 * affordance here: the whole tile is a single button, so the row must not
 * nest its own click target. No vertical margins either — a margin sits
 * outside the row, where the rail cannot draw, and would cut the timeline.
 */
export const SegmentRow = styled.div`
	display: flex;
	gap: 10px;
	align-items: stretch;
`

export const SegmentRail = styled.span`
	width: 24px;
	flex-shrink: 0;
	position: relative;
	display: flex;
	flex-direction: column;
	align-items: center;
	padding: 6px 0;
`

/* position: relative → paints above the connector runs. */
export const SegmentIcon = styled.span`
	width: 24px;
	height: 24px;
	border-radius: ${radii.round};
	background: ${SEGMENT_CHIP_BG};
	display: inline-flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	position: relative;

	svg {
		width: 14px;
		height: 14px;
		fill: ${SEGMENT_ICON};
	}
`

export const SegmentLabel = styled.span`
	${text.caption}
	color: ${SEGMENT_TEXT};
	align-self: center;
	min-width: 0;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
`
